/**
 * Decoration Asset URL Generator
 * 
 * Generates URLs for decoration assets (card images, card backs, backgrounds)
 * based on the asset management structure defined in DECORATION_ASSET_MANAGEMENT.md
 */

export interface DecorationAssetOptions {
  deckId: string;
  cardNumber?: number;
  cardSlug?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
  usePreview?: boolean;
  useFallback?: boolean;
}

/**
 * Get card image URL
 * @param deckId - Theme deck ID (e.g., 'rider-waite', 'mystic-dreams')
 * @param cardNumber - Card number (0-77)
 * @param cardSlug - Card slug (e.g., 'the-fool', 'the-magician')
 * @param usePreview - Use preview thumbnail instead of full image
 * @returns URL to card image
 */
export function getCardImageUrl(
  deckId: string,
  cardNumber?: number,
  cardSlug?: string,
  usePreview = false
): string {
  const basePath = `/assets/decorations/card-images/${deckId}`;
  
  if (usePreview) {
    // Use single preview for entire deck
    return `${basePath}/preview.webp`;
  }
  
  if (cardNumber !== undefined && cardSlug) {
    // Full card image
    const paddedNumber = cardNumber.toString().padStart(2, '0');
    return `${basePath}/${deckId}_${paddedNumber}_${cardSlug}.webp`;
  }
  
  // Default to preview if no card specified
  return `${basePath}/preview.webp`;
}

/**
 * Get card back URL
 * @param deckId - Theme deck ID
 * @param usePreview - Use preview thumbnail instead of full image
 * @returns URL to card back image
 */
export function getCardBackUrl(deckId: string, usePreview = false): string {
  if (usePreview) {
    return `/assets/decorations/card-backs/previews/${deckId}_back_preview.webp`;
  }
  return `/assets/decorations/card-backs/${deckId}_back.webp`;
}

/**
 * Get background URL
 * @param backgroundId - Background ID (e.g., 'starry-night', 'aurora-borealis')
 * @param deviceType - Device type for responsive images
 * @param usePreview - Use preview thumbnail instead of full image
 * @returns URL to background image
 */
export function getBackgroundUrl(
  backgroundId: string,
  deviceType: 'mobile' | 'tablet' | 'desktop' = 'mobile',
  usePreview = false
): string {
  const basePath = `/assets/decorations/backgrounds/${backgroundId}`;
  
  if (usePreview) {
    return `${basePath}/preview.webp`;
  }
  
  return `${basePath}/${backgroundId}_${deviceType}.webp`;
}

/**
 * Get decoration asset URL based on item metadata
 * @param item - Shop or inventory item with metadata
 * @param usePreview - Use preview thumbnail
 * @returns URL to decoration asset, or null if no valid URL can be determined
 */
export function getDecorationAssetUrl(
  item: any,
  usePreview = true
): string | null {
  const metadata = item.metadata || {};
  const itemDetails = metadata.itemDetails || {};
  
  // Get decoration type
  const decorationType = 
    metadata.decoration_type || 
    itemDetails.decoration_type || 
    itemDetails.category;
  
  // Get deck/background ID
  const deckId = metadata.deck_id || itemDetails.deck_id;
  const backgroundId = metadata.background_id || itemDetails.background_id;
  
  // Generate URL based on type
  switch (decorationType) {
    case 'card_images':
    case 'card_theme':
      if (deckId) {
        return getCardImageUrl(deckId, undefined, undefined, usePreview);
      }
      break;
      
    case 'card_back':
      if (deckId) {
        return getCardBackUrl(deckId, usePreview);
      }
      break;
      
    case 'background':
      if (backgroundId) {
        return getBackgroundUrl(backgroundId, 'mobile', usePreview);
      }
      break;
  }
  
  // Fallback: try to extract from item name
  return getFallbackAssetUrl(item, usePreview);
}

/**
 * Fallback: Extract deck/background ID from item name
 * @returns URL to decoration asset, or null if no match found
 */
function getFallbackAssetUrl(
  item: { metadata?: any; itemName?: string; name?: string },
  usePreview: boolean
): string | null {
  const metadata = item.metadata || {};
  const itemDetails = metadata.itemDetails || {};
  // Check multiple possible name fields (root level, itemDetails, metadata)
  const itemName = item.itemName || item.name || itemDetails.name || itemDetails.set_name || metadata.name || metadata.itemName || '';
  
  // Try to extract deck ID from name
  const nameLower = itemName.toLowerCase();
  
  // Card themes
  if (nameLower.includes('rider-waite') || nameLower.includes('rider waite') || nameLower.includes('classic')) {
    return getCardImageUrl('rider-waite', undefined, undefined, usePreview);
  }
  if (nameLower.includes('mystic dreams') || nameLower.includes('mystic-dreams')) {
    return getCardImageUrl('mystic-dreams', undefined, undefined, usePreview);
  }
  if (nameLower.includes('celestial wisdom') || nameLower.includes('celestial-wisdom')) {
    return getCardImageUrl('celestial-wisdom', undefined, undefined, usePreview);
  }
  if (nameLower.includes('dark gothic') || nameLower.includes('dark-gothic')) {
    return getCardImageUrl('dark-gothic', undefined, undefined, usePreview);
  }
  
  // Card backs
  if (nameLower.includes('golden celestial')) {
    return getCardBackUrl('golden-celestial', usePreview);
  }
  if (nameLower.includes('midnight moon')) {
    return getCardBackUrl('midnight-moon', usePreview);
  }
  if (nameLower.includes('rose garden')) {
    return getCardBackUrl('rose-garden', usePreview);
  }
  
  // Backgrounds
  if (nameLower.includes('starry night')) {
    return getBackgroundUrl('starry-night', 'mobile', usePreview);
  }
  if (nameLower.includes('aurora borealis')) {
    return getBackgroundUrl('aurora-borealis', 'mobile', usePreview);
  }
  if (nameLower.includes('mystic forest')) {
    return getBackgroundUrl('mystic-forest', 'mobile', usePreview);
  }
  
  // Default fallback - return null to trigger emoji fallback in UI
  return null;
}

/**
 * Get responsive background URL based on screen size
 * @param backgroundId - Background ID
 * @param usePreview - Use preview thumbnail
 * @returns URL to background image
 */
export function getResponsiveBackgroundUrl(
  backgroundId: string,
  usePreview = false
): string {
  if (usePreview) {
    return getBackgroundUrl(backgroundId, 'mobile', true);
  }
  
  // Detect device type based on screen width
  const width = window.innerWidth;
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'mobile';
  
  if (width >= 1024) {
    deviceType = 'desktop';
  } else if (width >= 768) {
    deviceType = 'tablet';
  }
  
  return getBackgroundUrl(backgroundId, deviceType, false);
}

/**
 * Preload decoration asset
 * @param url - Asset URL to preload
 */
export function preloadDecorationAsset(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Get decoration type emoji for display
 * @param decorationType - Type of decoration
 * @returns Emoji string
 */
export function getDecorationEmoji(decorationType: string): string {
  switch (decorationType) {
    case 'card_images':
    case 'card_theme':
      return '🎴';
    case 'card_back':
      return '🃏';
    case 'background':
      return '🌌';
    default:
      return '✨';
  }
}

/**
 * Get decoration type display name
 * @param decorationType - Type of decoration
 * @returns Display name
 */
export function getDecorationTypeName(decorationType: string): string {
  switch (decorationType) {
    case 'card_images':
    case 'card_theme':
      return 'Card Theme';
    case 'card_back':
      return 'Card Back';
    case 'background':
      return 'Background';
    default:
      return 'Decoration';
  }
}
